import { MOCK_KAMETI_GROUPS } from "@/mocks/groups";
import type {
  ApiResponse,
  CommissionType,
  DividendTiming,
  GroupCycleRow,
  GroupDraft,
  GroupQuery,
  GroupStatus,
  GroupTicketView,
  GroupView,
  GroupWonCycle,
  KametiGroup,
  MemberRemark,
  MemberStatus,
  PayoutStatus,
  ReplacedMemberView,
  ReplacementPreview,
  ReplaceMemberPayload,
} from "@/services/types";
import { ApiError } from "@/services/types";
import { listMembers } from "@/services/members";
import { USE_MOCK_API } from "@/services/config";
import { http } from "@/services/http";
import {
  buildCycles,
  canActivate,
  chitValue,
  completedCycles,
  nextAuctionDate,
  nextGroupCode,
  totalDiscount,
} from "@/utils/group";

/**
 * Centralized group service. Every screen calls through here — no screen performs
 * a direct data fetch. The real REST API swaps in behind these identical
 * signatures later without touching any screen.
 */

const delay = (ms = 350) => new Promise((resolve) => setTimeout(resolve, ms));

/** In-memory store seeded from mock data so CRUD works during this session. */
let store: KametiGroup[] = [...MOCK_KAMETI_GROUPS];

function findGroup(id: string): KametiGroup {
  const group = store.find((g) => g.id === id);
  if (!group) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");
  return group;
}

/**
 * Always reads the mock store directly, regardless of USE_MOCK_API — for
 * other modules that are still entirely mock (auctions — no real
 * auction-close endpoint exists yet) and need their group lookups to stay
 * consistent with mock-only mutations like recordWonCycle below, which
 * also always targets this same store. getGroup()/getGroupView() can't be
 * used for this: once USE_MOCK_API is false they call the real API, which
 * correctly rejects a mock seed's non-UUID group id.
 */
export function getGroupMockSnapshot(id: string): KametiGroup {
  return findGroup(id);
}

function padSeat(seat: number): string {
  return `T-${String(seat).padStart(2, "0")}`;
}

/* ================= Real API — backend shapes and mapping ================= */

interface BackendGroup {
  id: string;
  groupCode: string;
  groupName: string;
  installmentAmount: string;
  totalMembers: number;
  chitValue: string;
  startDate: string;
  auctionDay: number;
  installmentDueDay: number;
  organizerGmId: string | null;
  organizerCycleNo: number;
  commissionType: string;
  commissionValue: string;
  minDiscountPercent: string | null;
  maxDiscountPercent: string | null;
  bidStepAmount: string;
  blockBidIfOverdue: boolean;
  dividendApplyTo: string;
  graceDays: number;
  lateFeeValue: string;
  lateFeeIsPercent: boolean;
  lateFeePerDay: boolean;
  status: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BackendGroupMemberRow {
  groupMemberId: string;
  memberId: string;
  memberCode: string;
  fullName: string;
  phone: string;
  city: string | null;
  memberStatus: string;
  ticketNo: number;
  isOrganizer: boolean;
  hasWon: boolean;
  wonCycleNo: number | null;
  enrollmentStatus?: string;
  joinedOn?: string;
  exitedOn?: string | null;
  replacedBy?: string | null;
  remarks?: string | null;
  outstanding: string;
  notes?: MemberRemark[];
}

interface BackendCycleRow {
  cycleNo: number;
  kind: string;
  periodLabel: string;
  auctionDate: string | null;
  winnerGmId: string | null;
  winningBidAmount: string | null;
  discountAmount: string;
  commissionAmount?: string | null;
  dividendPerMember: string;
  netInstallment: string | null;
  status: string;
  totalDemanded?: number;
  totalCollected?: number;
  collectionStatus?: string;
  collectedPct?: number;
  payoutStatus?: string | null;
}

interface BackendMemberGroupRow {
  groupId: string;
  groupName: string;
  outstanding: string;
}

const COMMISSION_TYPE_FROM_BACKEND: Record<string, CommissionType> = {
  NONE: "none",
  PERCENT_OF_POOL: "pool_percentage",
  PERCENT_OF_DISCOUNT: "discount_percentage",
  FIXED_AMOUNT: "fixed",
};
const COMMISSION_TYPE_TO_BACKEND: Record<CommissionType, string> = {
  none: "NONE",
  pool_percentage: "PERCENT_OF_POOL",
  discount_percentage: "PERCENT_OF_DISCOUNT",
  fixed: "FIXED_AMOUNT",
};
const DIVIDEND_FROM_BACKEND: Record<string, DividendTiming> = { SAME_CYCLE: "same_month", NEXT_CYCLE: "next_month" };
const DIVIDEND_TO_BACKEND: Record<DividendTiming, string> = { same_month: "SAME_CYCLE", next_month: "NEXT_CYCLE" };
const STATUS_FROM_BACKEND: Record<string, GroupStatus> = {
  DRAFT: "draft",
  ACTIVE: "active",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

function mapGroup(bg: BackendGroup, organizerMemberId: string): KametiGroup {
  return {
    id: bg.id,
    groupCode: bg.groupCode,
    name: bg.groupName,
    monthlyInstallment: Number(bg.installmentAmount),
    memberCount: bg.totalMembers,
    chitValue: Number(bg.chitValue),
    startDate: bg.startDate,
    auctionDay: bg.auctionDay,
    dueDay: bg.installmentDueDay,
    organizerMemberId,
    reservedCycle: bg.organizerCycleNo,
    commissionType: COMMISSION_TYPE_FROM_BACKEND[bg.commissionType] ?? "none",
    commissionValue: Number(bg.commissionValue),
    minDiscountPct: bg.minDiscountPercent != null ? Number(bg.minDiscountPercent) : null,
    maxDiscountPct: bg.maxDiscountPercent != null ? Number(bg.maxDiscountPercent) : null,
    bidStep: Number(bg.bidStepAmount),
    blockOverdueBidding: bg.blockBidIfOverdue,
    dividendTiming: DIVIDEND_FROM_BACKEND[bg.dividendApplyTo] ?? "same_month",
    gracePeriodDays: bg.graceDays,
    lateFee: Number(bg.lateFeeValue),
    lateFeeIsPercent: bg.lateFeeIsPercent,
    lateFeePerDay: bg.lateFeePerDay,
    status: STATUS_FROM_BACKEND[bg.status] ?? "draft",
    tickets: [],
    wonCycles: [],
    // Not exposed by GET /groups/:id yet — only recorded in the audit
    // trail on cancel. A cancelled group's reason won't show here until
    // the backend surfaces it on the group record itself.
    cancelledReason: undefined,
    createdBy: bg.createdBy ?? "",
    createdAt: bg.createdAt,
    updatedBy: bg.createdBy ?? "",
    updatedAt: bg.updatedAt,
  };
}

function draftToBackendBody(draft: GroupDraft): Record<string, unknown> {
  return {
    groupName: draft.name,
    installmentAmount: draft.monthlyInstallment,
    totalMembers: draft.memberCount,
    startDate: draft.startDate,
    auctionDay: draft.auctionDay,
    installmentDueDay: draft.dueDay,
    organizerCycleNo: draft.reservedCycle,
    commissionType: COMMISSION_TYPE_TO_BACKEND[draft.commissionType],
    commissionValue: draft.commissionValue,
    minDiscountPercent: draft.minDiscountPct ?? undefined,
    maxDiscountPercent: draft.maxDiscountPct ?? undefined,
    bidStepAmount: draft.bidStep,
    blockBidIfOverdue: draft.blockOverdueBidding,
    dividendApplyTo: DIVIDEND_TO_BACKEND[draft.dividendTiming],
    graceDays: draft.gracePeriodDays,
    lateFeeValue: draft.lateFee,
    lateFeeIsPercent: draft.lateFeeIsPercent,
    lateFeePerDay: draft.lateFeePerDay,
  };
}

async function fetchGroupMembers(groupId: string): Promise<BackendGroupMemberRow[]> {
  return http<BackendGroupMemberRow[]>(`/groups/${groupId}/members`, { method: "GET" });
}

/** Replaced rows keep their ticket number for history — only the active holder owns the ticket. */
function activeTickets(members: BackendGroupMemberRow[]) {
  return members
    .filter((m) => (m.enrollmentStatus ?? "ACTIVE") === "ACTIVE")
    .map((m) => ({ ticketNumber: String(m.ticketNo), memberId: m.memberId }));
}

function resolveOrganizerMemberId(organizerGmId: string | null, members: BackendGroupMemberRow[]): string {
  if (!organizerGmId) return "";
  return members.find((m) => m.groupMemberId === organizerGmId)?.memberId ?? "";
}

function extractWonCycles(bg: BackendGroup, members: BackendGroupMemberRow[], cycleRows: BackendCycleRow[]): GroupWonCycle[] {
  return cycleRows
    .filter((c) => ["AUCTION_CLOSED", "COLLECTING", "PAYOUT_DONE", "CLOSED"].includes(c.status) || Boolean(c.winnerGmId))
    .map((c) => {
      const winnerMember = c.winnerGmId ? members.find((m) => m.groupMemberId === c.winnerGmId) : null;
      return {
        cycle: c.cycleNo,
        memberId: winnerMember?.memberId ?? "",
        winningBid: c.winningBidAmount != null ? Number(c.winningBidAmount) : Number(bg.chitValue),
      };
    });
}

/** Assembles the full GroupView from the base group + its members + its cycles — the real-API equivalent of the mock's toView(). */
async function assembleGroupView(bg: BackendGroup, members: BackendGroupMemberRow[]): Promise<GroupView> {
  const organizerMemberId = resolveOrganizerMemberId(bg.organizerGmId, members);
  const group = mapGroup(bg, organizerMemberId);
  group.tickets = activeTickets(members);

  // Per-ticket "outstanding in other groups" — N+1 across ticket holders,
  // same tradeoff already accepted in services/members.ts's listMembers().
  const otherGroupsByMember = new Map<string, { groupId: string; groupName: string; outstanding: number }[]>();
  await Promise.all(
    members.map(async (m) => {
      const rows = await http<BackendMemberGroupRow[]>(`/members/${m.memberId}/groups`, { method: "GET" });
      otherGroupsByMember.set(
        m.memberId,
        rows
          .filter((r) => r.groupId !== bg.id)
          .map((r) => ({ groupId: r.groupId, groupName: r.groupName, outstanding: Number(r.outstanding) }))
      );
    })
  );

  const seats: GroupTicketView[] = [];
  for (let seat = 1; seat <= group.memberCount; seat++) {
    const m = members.find((row) => row.ticketNo === seat && (row.enrollmentStatus ?? "ACTIVE") === "ACTIVE");
    if (!m) {
      seats.push({
        seat,
        ticketNumber: padSeat(seat),
        memberId: "",
        member: null,
        isOrganizer: false,
        wonCycle: null,
        outstanding: 0,
        otherGroups: [],
      });
      continue;
    }
    seats.push({
      seat,
      ticketNumber: String(m.ticketNo),
      memberId: m.memberId,
      groupMemberId: m.groupMemberId,
      enrollmentStatus: m.enrollmentStatus ?? "ACTIVE",
      member: {
        id: m.memberId,
        memberCode: m.memberCode,
        fullName: m.fullName,
        primaryPhone: m.phone,
        city: m.city ?? "",
        status: m.memberStatus.toLowerCase() as MemberStatus,
      },
      isOrganizer: m.isOrganizer,
      wonCycle: m.hasWon ? m.wonCycleNo : null,
      outstanding: Number(m.outstanding),
      otherGroups: otherGroupsByMember.get(m.memberId) ?? [],
      remarks: m.notes ?? [],
    });
  }

  const replacedMembers: ReplacedMemberView[] = members
    .filter((m) => m.enrollmentStatus === "REPLACED")
    .map((m) => ({
      groupMemberId: m.groupMemberId,
      memberId: m.memberId,
      memberCode: m.memberCode,
      fullName: m.fullName,
      phone: m.phone,
      ticketNo: m.ticketNo,
      joinedOn: m.joinedOn ?? "",
      exitedOn: m.exitedOn,
      replacedBy: m.replacedBy,
      remarks: m.remarks,
      outstanding: Number(m.outstanding),
    }));

  const cycleRows = await http<BackendCycleRow[]>(`/groups/${bg.id}/cycles`, { method: "GET" });
  group.wonCycles = extractWonCycles(bg, members, cycleRows);
  const cycles: GroupCycleRow[] = cycleRows.map((c) => {
    const commissionTotal = c.commissionAmount != null ? Number(c.commissionAmount) : 0;
    const commPerMember = group.memberCount > 0 ? Math.floor(commissionTotal / group.memberCount) : 0;
    let collectionStatus: "pending" | "partial" | "paid" | "overdue" = "pending";
    if (c.collectionStatus) {
      const s = c.collectionStatus.toLowerCase();
      if (s === "paid" || s === "completed") collectionStatus = "paid";
      else if (s === "partial" || s === "collecting") collectionStatus = "partial";
      else if (s === "overdue") collectionStatus = "overdue";
      else collectionStatus = "pending";
    }

    let payoutStatus: PayoutStatus | "pending" = "pending";
    if (c.payoutStatus) {
      const ps = c.payoutStatus.toLowerCase();
      if (ps === "paid" || ps === "disbursed") payoutStatus = "disbursed";
      else if (ps === "partially_paid" || ps === "partially_disbursed") payoutStatus = "partially_disbursed";
      else if (ps === "approved") payoutStatus = "approved";
      else if (ps === "pending_approval") payoutStatus = "pending_approval";
      else if (ps === "reversed") payoutStatus = "reversed";
      else payoutStatus = "pending";
    }

    return {
      cycle: c.cycleNo,
      periodLabel: c.periodLabel,
      isOrganizerReserved: c.kind === "ORGANIZER_RESERVED",
      winnerMemberId: c.winnerGmId ? (members.find((m) => m.groupMemberId === c.winnerGmId)?.memberId ?? null) : null,
      winningBid: c.winningBidAmount != null ? Number(c.winningBidAmount) : 0,
      discount: Number(c.discountAmount),
      perMemberDividend: Number(c.dividendPerMember),
      commissionPerMember: commPerMember,
      commissionAmount: commissionTotal,
      netInstallment: c.netInstallment != null ? Number(c.netInstallment) : group.monthlyInstallment,
      status: ["AUCTION_CLOSED", "COLLECTING", "PAYOUT_DONE", "CLOSED"].includes(c.status) || Boolean(c.winnerGmId) ? "completed" : "pending",
      totalDemanded: c.totalDemanded != null ? Number(c.totalDemanded) : 0,
      totalCollected: c.totalCollected != null ? Number(c.totalCollected) : 0,
      collectionStatus,
      collectedPct: c.collectedPct != null ? Number(c.collectedPct) : 0,
      payoutStatus,
    };
  });

  const completed = cycles.filter((c) => c.status === "completed");
  const nextPendingCycle = cycles.find((c) => c.status === "pending");
  const nextPendingRow = nextPendingCycle ? cycleRows.find((c) => c.cycleNo === nextPendingCycle.cycle) : undefined;

  return {
    ...group,
    tickets: seats,
    cycles,
    completedCycles: completed.length,
    totalDiscount: completed.reduce((sum, c) => sum + c.discount, 0),
    nextAuctionDate: nextPendingRow?.auctionDate ?? null,
    replacedMembers,
  };
}

/** Resolve a group into a full view with member details, empty seats and cycles. */
async function toView(group: KametiGroup): Promise<GroupView> {
  const members = await listMembers();
  const memberMap = new Map(members.map((m) => [m.id, m]));
  const seats: GroupTicketView[] = [];
  for (let seat = 1; seat <= group.memberCount; seat++) {
    const ticket = group.tickets[seat - 1];
    if (!ticket) {
      seats.push({
        seat,
        ticketNumber: padSeat(seat),
        memberId: "",
        member: null,
        isOrganizer: false,
        wonCycle: null,
        outstanding: 0,
        otherGroups: [],
      });
      continue;
    }
    const m = memberMap.get(ticket.memberId);
    const enrollment = m?.enrollments.find((e) => e.groupId === group.id);
    const won = group.wonCycles.find((w) => w.memberId === ticket.memberId);
    seats.push({
      seat,
      ticketNumber: ticket.ticketNumber,
      memberId: ticket.memberId,
      member: m
        ? {
            id: m.id,
            memberCode: m.memberCode,
            fullName: m.fullName,
            primaryPhone: m.primaryPhone,
            city: m.city,
            status: m.status,
          }
        : null,
      isOrganizer: ticket.memberId === group.organizerMemberId,
      wonCycle: won?.cycle ?? null,
      outstanding: enrollment?.outstanding ?? 0,
      otherGroups: (m?.enrollments ?? [])
        .filter((e) => e.groupId !== group.id)
        .map((e) => ({ groupId: e.groupId, groupName: e.groupName, outstanding: e.outstanding })),
    });
  }
  const cycles: GroupCycleRow[] = buildCycles(group);
  return {
    ...group,
    tickets: seats,
    cycles,
    completedCycles: completedCycles(group),
    totalDiscount: totalDiscount(group),
    nextAuctionDate: nextAuctionDate(group),
  };
}

/** Enforce the enrollment rules that must always hold. Returns the member error, if any. */
function assertAssignable(group: KametiGroup, memberId: string, members: { id: string; status: string }[]): void {
  if (group.tickets.some((t) => t.memberId === memberId)) {
    throw new ApiError("This member already holds a ticket in this group.", 409, "MEMBER_DUPLICATE");
  }
  if (group.tickets.length >= group.memberCount) {
    throw new ApiError("All tickets are already filled.", 409, "TICKETS_FULL");
  }
  const member = members.find((m) => m.id === memberId);
  if (member?.status === "blacklisted") {
    throw new ApiError(
      "Blacklisted members cannot be assigned a ticket. A member's status must be cleared before enrolment.",
      409,
      "MEMBER_BLACKLISTED"
    );
  }
}

export async function listGroups(query: GroupQuery = {}): Promise<KametiGroup[]> {
  if (!USE_MOCK_API) {
    const params = new URLSearchParams();
    if (query.search) params.set("search", query.search);
    if (query.status) params.set("status", query.status.toUpperCase());
    params.set("pageSize", "100");

    let page = 1;
    let all: BackendGroup[] = [];
    for (;;) {
      params.set("page", String(page));
      const envelope = await http<{ data: BackendGroup[]; meta: { totalPages: number } }>(
        `/groups?${params.toString()}`,
        { method: "GET", raw: true }
      );
      all = all.concat(envelope.data);
      if (page >= envelope.meta.totalPages) break;
      page += 1;
    }

    return all.map((bg: any) => {
      const tickets = Array.isArray(bg.tickets) ? bg.tickets : [];
      const wonCycles = Array.isArray(bg.wonCycles) ? bg.wonCycles : [];
      const organizerMemberId = bg.organizerMemberId ?? "";
      const group = mapGroup(bg, organizerMemberId);
      group.tickets = tickets;
      group.wonCycles = wonCycles;
      return group;
    });
  }

  await delay();
  let out = [...store];
  if (query.search) {
    const q = query.search.trim().toLowerCase();
    out = out.filter((g) => g.name.toLowerCase().includes(q) || g.groupCode.toLowerCase().includes(q));
  }
  if (query.status) out = out.filter((g) => g.status === query.status);
  return out;
}

export async function getGroup(id: string): Promise<KametiGroup> {
  if (!USE_MOCK_API) {
    const [bg, members, cycleRows] = await Promise.all([
      http<BackendGroup>(`/groups/${id}`, { method: "GET" }),
      fetchGroupMembers(id),
      http<BackendCycleRow[]>(`/groups/${id}/cycles`, { method: "GET" }),
    ]);
    const group = mapGroup(bg, resolveOrganizerMemberId(bg.organizerGmId, members));
    group.tickets = activeTickets(members);
    group.wonCycles = extractWonCycles(bg, members, cycleRows);
    return group;
  }

  await delay(150);
  return findGroup(id);
}

export async function getGroupView(id: string): Promise<GroupView> {
  if (!USE_MOCK_API) {
    const [bg, members] = await Promise.all([
      http<BackendGroup>(`/groups/${id}`, { method: "GET" }),
      fetchGroupMembers(id),
    ]);
    return assembleGroupView(bg, members);
  }

  await delay();
  return toView(findGroup(id));
}

export async function createGroup(draft: GroupDraft, actor: string): Promise<GroupView> {
  if (!USE_MOCK_API) {
    const bg = await http<BackendGroup>("/groups", { method: "POST", body: draftToBackendBody(draft) });
    // The wizard requires the organizer to be chosen as part of creation,
    // but the backend only has an organizer once someone is actually
    // enrolled with isOrganizer=true — so reserve ticket 1 for them here,
    // immediately, rather than leaving that as a dangling client-side
    // "intended organizer" the enrollment page would have to know about.
    await http(`/groups/${bg.id}/members`, {
      method: "POST",
      body: { memberId: draft.organizerMemberId, ticketNo: 1, isOrganizer: true },
    });
    return getGroupView(bg.id);
  }

  await delay(500);
  const now = new Date().toISOString();
  const group: KametiGroup = {
    ...draft,
    id: `g-${Date.now()}`,
    groupCode: nextGroupCode(store.length + 1),
    chitValue: chitValue(draft.monthlyInstallment, draft.memberCount),
    status: "draft",
    tickets: [],
    wonCycles: [],
    createdBy: actor,
    createdAt: now,
    updatedBy: actor,
    updatedAt: now,
  };
  store = [group, ...store];
  return toView(group);
}

export async function updateGroup(id: string, draft: GroupDraft, actor: string): Promise<GroupView> {
  if (!USE_MOCK_API) {
    await http<BackendGroup>(`/groups/${id}`, { method: "PATCH", body: draftToBackendBody(draft) });
    return getGroupView(id);
  }

  await delay(500);
  const index = store.findIndex((g) => g.id === id);
  if (index === -1) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");
  const existing = store[index];
  if (existing.status !== "draft") {
    throw new ApiError(
      "This group is no longer a draft. Installment amount, member count, duration and reserved cycle are locked once active.",
      409,
      "GROUP_LOCKED"
    );
  }
  const updated: KametiGroup = {
    ...existing,
    ...draft,
    id: existing.id,
    groupCode: existing.groupCode,
    chitValue: chitValue(draft.monthlyInstallment, draft.memberCount),
    status: "draft",
    tickets: existing.tickets,
    wonCycles: existing.wonCycles,
    updatedBy: actor,
    updatedAt: new Date().toISOString(),
  };
  store = store.map((g) => (g.id === id ? updated : g));
  return toView(updated);
}

export async function addTicket(groupId: string, memberId: string, seat?: number): Promise<GroupView> {
  if (!USE_MOCK_API) {
    const [bg, members] = await Promise.all([
      http<BackendGroup>(`/groups/${groupId}`, { method: "GET" }),
      fetchGroupMembers(groupId),
    ]);
    const organizerMemberId = resolveOrganizerMemberId(bg.organizerGmId, members);
    const taken = new Set(members.map((m) => m.ticketNo));
    let ticketNo = seat && !taken.has(seat) ? seat : 1;
    while (taken.has(ticketNo)) ticketNo += 1;

    await http(`/groups/${groupId}/members`, {
      method: "POST",
      body: { memberId, ticketNo, isOrganizer: memberId === organizerMemberId },
    });
    return getGroupView(groupId);
  }

  await delay(300);
  const group = findGroup(groupId);
  if (group.status !== "draft") {
    throw new ApiError("Tickets can only be changed while the group is a draft.", 409, "GROUP_LOCKED");
  }
  const members = await listMembers();
  assertAssignable(group, memberId, members);
  const s = seat ?? group.tickets.length + 1;
  const updated: KametiGroup = {
    ...group,
    tickets: [...group.tickets, { ticketNumber: padSeat(s), memberId }],
    updatedAt: new Date().toISOString(),
  };
  store = store.map((g) => (g.id === groupId ? updated : g));
  return toView(updated);
}

export async function bulkAssignTickets(groupId: string, memberIds: string[]): Promise<GroupView> {
  if (!USE_MOCK_API) {
    // Doesn't set isOrganizer for anyone in the batch — fine in the
    // intended flow, since the organizer is already enrolled at group
    // creation (see createGroup) before bulk-assign is ever used.
    await http(`/groups/${groupId}/members/bulk`, { method: "POST", body: { memberIds } });
    return getGroupView(groupId);
  }

  await delay(400);
  const group = findGroup(groupId);
  if (group.status !== "draft") {
    throw new ApiError("Tickets can only be changed while the group is a draft.", 409, "GROUP_LOCKED");
  }
  const members = await listMembers();
  let working = { ...group };
  for (const memberId of memberIds) {
    assertAssignable(working, memberId, members);
    const seat = working.tickets.length + 1;
    working = {
      ...working,
      tickets: [...working.tickets, { ticketNumber: padSeat(seat), memberId }],
    };
  }
  const updated: KametiGroup = {
    ...working,
    updatedAt: new Date().toISOString(),
  };
  store = store.map((g) => (g.id === groupId ? updated : g));
  return toView(updated);
}

export async function removeTicket(groupId: string, memberId: string): Promise<GroupView> {
  if (!USE_MOCK_API) {
    const members = await fetchGroupMembers(groupId);
    const target = members.find((m) => m.memberId === memberId);
    if (!target) {
      throw new ApiError("This member does not hold a ticket in this group.", 404, "NOT_FOUND");
    }
    if (target.isOrganizer) {
      throw new ApiError("The organizer must hold a ticket. Remove the organizer assignment first.", 409, "ORGANIZER_TICKET");
    }
    await http<void>(`/groups/${groupId}/members/${target.groupMemberId}`, { method: "DELETE" });
    return getGroupView(groupId);
  }

  await delay(300);
  const group = findGroup(groupId);
  if (group.status !== "draft") {
    throw new ApiError("Tickets can only be changed while the group is a draft.", 409, "GROUP_LOCKED");
  }
  if (group.organizerMemberId === memberId) {
    throw new ApiError("The organizer must hold a ticket. Remove the organizer assignment first.", 409, "ORGANIZER_TICKET");
  }
  const updated: KametiGroup = {
    ...group,
    tickets: group.tickets.filter((t) => t.memberId !== memberId),
    updatedAt: new Date().toISOString(),
  };
  store = store.map((g) => (g.id === groupId ? updated : g));
  return toView(updated);
}

export async function cancelGroup(id: string, reason: string, actor: string): Promise<GroupView> {
  if (!USE_MOCK_API) {
    await http<BackendGroup>(`/groups/${id}/cancel`, { method: "POST", body: { reason } });
    return getGroupView(id);
  }

  await delay(400);
  const index = store.findIndex((g) => g.id === id);
  if (index === -1) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");
  const existing = store[index];
  if (existing.status === "completed") {
    throw new ApiError("A completed group cannot be cancelled.", 409, "GROUP_COMPLETED");
  }
  const updated: KametiGroup = {
    ...existing,
    status: "cancelled",
    cancelledReason: reason,
    updatedBy: actor,
    updatedAt: new Date().toISOString(),
  };
  store = store.map((g) => (g.id === id ? updated : g));
  return toView(updated);
}

export async function activateGroup(id: string, actor: string): Promise<GroupView> {
  if (!USE_MOCK_API) {
    await http<BackendGroup>(`/groups/${id}/activate`, { method: "POST" });
    return getGroupView(id);
  }

  await delay(600);
  const index = store.findIndex((g) => g.id === id);
  if (index === -1) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");
  const existing = store[index];
  if (existing.status !== "draft") {
    throw new ApiError("Only a draft group can be activated.", 409, "GROUP_NOT_DRAFT");
  }
  if (!canActivate(existing)) {
    const allFilled = existing.tickets.length >= existing.memberCount;
    const organizerAssigned = existing.tickets.some((t) => t.memberId === existing.organizerMemberId);
    throw new ApiError(
      `Cannot activate. ${!allFilled ? "Every ticket must be filled." : ""} ${!organizerAssigned ? "The organizer must hold a ticket." : ""}`.trim(),
      409,
      "GROUP_INCOMPLETE"
    );
  }
  const updated: KametiGroup = {
    ...existing,
    status: "active",
    updatedBy: actor,
    updatedAt: new Date().toISOString(),
  };
  store = store.map((g) => (g.id === id ? updated : g));
  return toView(updated);
}

/**
 * Record a decided cycle (auction or reserved organizer cycle) into an active
 * group. Used by the auction module when closing a cycle, keeping the group's
 * won-cycles list consistent with the auction records. Append-only.
 *
 * Stays mock-only regardless of USE_MOCK_API — there's no real auction-close
 * endpoint yet (a later stage); cycles are generated at activation but never
 * closed by anything real yet.
 */
export async function recordWonCycle(
  groupId: string,
  cycle: number,
  memberId: string,
  winningBid: number,
  actor: string
): Promise<GroupView> {
  await delay(200);
  const index = store.findIndex((g) => g.id === groupId);
  if (index === -1) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");
  const existing = store[index];
  if (existing.status !== "active") {
    throw new ApiError("Only an active group can have an auction result recorded.", 409, "GROUP_NOT_ACTIVE");
  }
  if (existing.wonCycles.some((w) => w.cycle === cycle)) {
    throw new ApiError("This cycle already has a result recorded.", 409, "CYCLE_DONE");
  }
  const updated: KametiGroup = {
    ...existing,
    wonCycles: [...existing.wonCycles, { cycle, memberId, winningBid }],
    updatedBy: actor,
    updatedAt: new Date().toISOString(),
  };
  store = store.map((g) => (g.id === groupId ? updated : g));
  return toView(updated);
}

/** Operational policy fields stay editable after activation (grace, late fee, caps, overdue block). */
export async function updatePolicies(
  id: string,
  patch: Partial<
    Pick<
      KametiGroup,
      | "gracePeriodDays"
      | "lateFee"
      | "lateFeeIsPercent"
      | "lateFeePerDay"
      | "minDiscountPct"
      | "maxDiscountPct"
      | "blockOverdueBidding"
    >
  >,
  actor: string
): Promise<GroupView> {
  if (!USE_MOCK_API) {
    const body: Record<string, unknown> = {};
    if (patch.gracePeriodDays !== undefined) body.graceDays = patch.gracePeriodDays;
    if (patch.lateFee !== undefined) body.lateFeeValue = patch.lateFee;
    if (patch.lateFeeIsPercent !== undefined) body.lateFeeIsPercent = patch.lateFeeIsPercent;
    if (patch.lateFeePerDay !== undefined) body.lateFeePerDay = patch.lateFeePerDay;
    if (patch.minDiscountPct !== undefined) body.minDiscountPercent = patch.minDiscountPct;
    if (patch.maxDiscountPct !== undefined) body.maxDiscountPercent = patch.maxDiscountPct;
    if (patch.blockOverdueBidding !== undefined) body.blockBidIfOverdue = patch.blockOverdueBidding;
    await http<BackendGroup>(`/groups/${id}`, { method: "PATCH", body });
    return getGroupView(id);
  }

  await delay(400);
  const index = store.findIndex((g) => g.id === id);
  if (index === -1) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");
  const existing = store[index];
  const updated: KametiGroup = {
    ...existing,
    ...patch,
    updatedBy: actor,
    updatedAt: new Date().toISOString(),
  };
  store = store.map((g) => (g.id === id ? updated : g));
  return toView(updated);
}

export async function getReplacementPreview(
  groupId: string,
  groupMemberId: string
): Promise<ReplacementPreview> {
  return http<ReplacementPreview>(`/groups/${groupId}/members/${groupMemberId}/replacement-preview`, {
    method: "GET",
  });
}

export async function replaceMember(
  groupId: string,
  groupMemberId: string,
  payload: ReplaceMemberPayload
): Promise<unknown> {
  return http(`/groups/${groupId}/members/${groupMemberId}/replace`, {
    method: "POST",
    body: payload,
  });
}

export interface ChangeTicketResult {
  groupMemberId: string;
  oldTicketNo: number;
  ticketNo: number;
  swappedWith: { groupMemberId: string; memberId: string; ticketNo: number } | null;
}

/** Moves a member to another ticket number; if that ticket is held, the two members swap. */
export async function changeTicketNo(
  groupId: string,
  groupMemberId: string,
  ticketNo: number
): Promise<ChangeTicketResult> {
  return http<ChangeTicketResult>(`/groups/${groupId}/members/${groupMemberId}/ticket`, {
    method: "PATCH",
    body: { ticketNo },
  });
}

/** Adds a remark; the response is the ticket's current remarks (newest 3), newest first. */
export async function addMemberRemark(
  groupId: string,
  groupMemberId: string,
  payload: { remark: string; followUpDate?: string }
): Promise<MemberRemark[]> {
  return http<MemberRemark[]>(`/groups/${groupId}/members/${groupMemberId}/remarks`, {
    method: "POST",
    body: payload,
  });
}

export async function deleteMemberRemark(groupId: string, groupMemberId: string, remarkId: string): Promise<void> {
  await http<void>(`/groups/${groupId}/members/${groupMemberId}/remarks/${remarkId}`, { method: "DELETE" });
}

export async function expandAndAddMember(
  groupId: string,
  memberIds: string | string[]
): Promise<unknown> {
  const payload = Array.isArray(memberIds)
    ? { memberIds }
    : { memberId: memberIds };
  return http(`/groups/${groupId}/add-member`, {
    method: "POST",
    body: payload,
  });
}

export async function deleteGroup(id: string): Promise<void> {
  if (!USE_MOCK_API) {
    await http<void>(`/groups/${id}`, { method: "DELETE" });
    return;
  }
  await delay(300);
  store = store.filter((g) => g.id !== id);
}

/** Convenience wrappers matching the service-layer response convention. */
export const groupService = {
  listGroups,
  getGroup,
  getGroupView,
  createGroup,
  updateGroup,
  deleteGroup,
  addTicket,
  bulkAssignTickets,
  removeTicket,
  cancelGroup,
  activateGroup,
  updatePolicies,
  recordWonCycle,
  getReplacementPreview,
  replaceMember,
  changeTicketNo,
  addMemberRemark,
  deleteMemberRemark,
  expandAndAddMember,
  toView,
};

export type { ApiResponse };
